import { useState, useMemo, useEffect } from 'react';

import styled from 'styled-components';
import { useSearchParams } from 'react-router-dom';
import Switch from 'react-switch';

import { colors } from 'constants/colors';
import { PAGES } from '../../constants/pages';
import {
    useGetDailyDeliveriesWithPhotosQuery,
    useGetTasksWithPhotosQuery,
    TaskPhotoType,
    PhotoState,
    PhotoIssueTypeInput,
    PhotoIssueTypeKey,
    SiteState,
} from 'data/__generated__';

import { Header, HeaderTitle } from 'components/Header';
import { Loader } from 'components/Loader';
import { PageTitle } from 'components/PageTitle';
import { TotemDateRangePicker } from 'components/TotemDateRangePicker';
import { TotemInput } from 'components/TotemInput';
import { TotemSelect, Option } from 'components/TotemSelect';
import { UltraSelect } from 'components/UltraSelect';
import { siteSource } from 'data/entity-sources/sources';
import { SegmentedControl } from 'components/SegmentedControl';
import { formatDateAsAnniversary, formatDateForDisplay } from 'helpers/dateTimes';
import { TotemGallery } from 'components/TotemGallery';
import { TASK_TYPE_LABELS } from 'pages/Tournees/helpers';
import {
    PhotosSectionContent,
    filterPhotos,
    hasPhotos,
    hasPhotosAfterFilters,
} from './DeliveryDetails/PhotosSectionContent';
import { DetailLink } from 'components/DetailsView/DetailLink';
import { SectionContainer } from 'components/DetailsView/Section';
import { TotemLabel } from 'components/TotemLabel';
import { parseUrlSearchParams, buildUrlSearchParams } from 'helpers/SearchParams/utils';

// Constante de module : une source se compare par référence dans les dépendances d'effet.
const TOTOPS_SITE_SOURCE = siteSource.withScope({
    states: [SiteState.Regular],
    types: null,
    shouldOperateOnTotops: true,
});

const STATE_OPTIONS: Option<PhotoState>[] = [
    { value: PhotoState.Pending, label: 'En attente' },
    { value: PhotoState.Validated, label: 'Validées' },
    { value: PhotoState.Refused, label: 'Refusées' },
];

const ISSUE_TYPE_OPTIONS: Option<PhotoIssueTypeKey>[] = [
    { value: PhotoIssueTypeKey.IsBadQuality, label: 'Mauvaise qualité' },
    { value: PhotoIssueTypeKey.IsBadQuantity, label: 'Mauvaise quantité' },
];

const STOP_TYPE_OPTIONS: Option<'delivery' | 'task'>[] = [
    { value: 'delivery', label: 'Livraisons' },
    { value: 'task', label: 'Tâches' },
];

type PhotoTimeFilter = 'before' | 'after' | 'all';

const PHOTO_TIME_OPTIONS: { value: PhotoTimeFilter; label: string }[] = [
    { value: 'before', label: 'Avant' },
    { value: 'after', label: 'Après' },
    { value: 'all', label: 'Toutes' },
];

type ParamTypes = {
    filterString?: string[];
};

export const DeliveriesDailyPhotos = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const params = parseUrlSearchParams<ParamTypes>(searchParams);
    const urlFilterString = params.filterString?.[0] || '';

    const [confirmedStartDate, setConfirmedStartDate] = useState<Date | null>(new Date());
    const [confirmedEndDate, setConfirmedEndDate] = useState<Date | null>(new Date());
    const [selectedStates, setSelectedStates] = useState<PhotoState[]>([]);
    const [selectedIssueKeys, setSelectedIssueKeys] = useState<PhotoIssueTypeKey[]>([]);
    const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);
    const [selectedOrganizationName, setSelectedOrganizationName] = useState<string | null>(null);
    const [selectedStopType, setSelectedStopType] = useState<'delivery' | 'task' | null>(null);
    const [hideEmptySites, setHideEmptySites] = useState<boolean>(true);
    const [photoTimeFilter, setPhotoTimeFilter] = useState<PhotoTimeFilter>('all');
    const [searchValue, setSearchValue] = useState<string>(urlFilterString);
    const [debouncedSearchValue, setDebouncedSearchValue] = useState<string>(urlFilterString);

    useEffect(() => {
        if (urlFilterString !== searchValue) {
            setSearchValue(urlFilterString);
            setDebouncedSearchValue(urlFilterString);
        }
    }, [urlFilterString]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchValue(searchValue);
            const searchParams = buildUrlSearchParams({
                ...params,
                filterString: searchValue.trim() ? [searchValue.trim()] : [],
            });
            setSearchParams(searchParams, { replace: true });
        }, 300);

        return () => clearTimeout(timer);
    }, [searchValue, params, setSearchParams]);

    const selectedIssueTypes = useMemo<PhotoIssueTypeInput[]>(
        () =>
            selectedIssueKeys.map((k) =>
                k === PhotoIssueTypeKey.IsBadQuality
                    ? { isBadQuality: true, isBadQuantity: false }
                    : { isBadQuality: false, isBadQuantity: true },
            ),
        [selectedIssueKeys],
    );

    const startDate = confirmedStartDate ? formatDateAsAnniversary({ dateTime: confirmedStartDate }) : null;
    const endDate = confirmedEndDate ? formatDateAsAnniversary({ dateTime: confirmedEndDate }) : null;

    const {
        data: deliveriesData,
        loading: deliveriesLoading,
        error: deliveriesError,
    } = useGetDailyDeliveriesWithPhotosQuery({
        fetchPolicy: 'cache-and-network',
        variables: { startDate, endDate },
    });

    const { data: tasksData } = useGetTasksWithPhotosQuery({
        fetchPolicy: 'cache-and-network',
        skip: !startDate || !endDate,
        variables: { startDate: startDate ?? '', endDate: endDate ?? '' },
    });

    const organizationOptions = useMemo<Option<string>[]>(
        () =>
            [
                ...new Set(
                    (deliveriesData?.deliveries ?? [])
                        .map(({ organizationName }) => organizationName)
                        .filter((organizationName): organizationName is string => !!organizationName),
                ),
            ]
                .sort((nameA, nameB) => nameA.localeCompare(nameB))
                .map((organizationName) => ({ label: organizationName, value: organizationName })),
        [deliveriesData],
    );

    const deliveries = useMemo(() => {
        if (selectedStopType === 'task') return [];
        const lowerSearch = debouncedSearchValue.trim().toLowerCase();
        return (deliveriesData?.deliveries ?? []).filter((delivery) => {
            const matchesSearch =
                !lowerSearch ||
                delivery.siteName?.toLowerCase().includes(lowerSearch) ||
                delivery.organizationName?.toLowerCase().includes(lowerSearch) ||
                delivery.label.toLowerCase().includes(lowerSearch);
            const matchesSite = !selectedSiteId || delivery.siteId === selectedSiteId;
            const matchesOrganization =
                !selectedOrganizationName || delivery.organizationName === selectedOrganizationName;

            return matchesSearch && matchesSite && matchesOrganization;
        });
    }, [deliveriesData, debouncedSearchValue, selectedSiteId, selectedOrganizationName, selectedStopType]);

    const tasks = useMemo(() => {
        if (selectedStopType === 'delivery') return [];
        const lowerSearch = debouncedSearchValue.trim().toLowerCase();
        return (tasksData?.tasks ?? []).filter(
            ({ site }) =>
                (!lowerSearch ||
                    site?.name.toLowerCase().includes(lowerSearch) ||
                    site?.organizationName.toLowerCase().includes(lowerSearch)) &&
                (!selectedSiteId || site?._id === selectedSiteId) &&
                (!selectedOrganizationName || site?.organizationName === selectedOrganizationName),
        );
    }, [tasksData, debouncedSearchValue, selectedSiteId, selectedOrganizationName, selectedStopType]);

    const deliveriesWithPhotosCount = deliveries.filter(hasPhotos).length;

    const showBeforePhotos = photoTimeFilter === 'all' || photoTimeFilter === 'before';
    const showAfterPhotos = photoTimeFilter === 'all' || photoTimeFilter === 'after';

    const filteredDeliveries = hideEmptySites
        ? deliveries.filter((delivery) =>
              hasPhotosAfterFilters(delivery, selectedStates, selectedIssueTypes, showBeforePhotos, showAfterPhotos),
          )
        : deliveries;

    const sortedDeliveries = [...filteredDeliveries].sort((deliveryA, deliveryB) =>
        (deliveryA.organizationName || deliveryA.siteName || deliveryA.label).localeCompare(
            deliveryB.organizationName || deliveryB.siteName || deliveryB.label,
        ),
    );

    const taskSections = tasks
        .map((task) => ({
            task,
            instructions: task.instructions
                .map(({ _id, label, controlPhotos }) => ({
                    _id,
                    label,
                    photos: filterPhotos(
                        controlPhotos.filter(({ type }) =>
                            type === TaskPhotoType.TaskStartPhoto ? showBeforePhotos : showAfterPhotos,
                        ),
                        selectedStates,
                        selectedIssueTypes,
                    ),
                }))
                .filter(({ photos }) => photos.length > 0),
        }))
        .filter(({ instructions }) => !hideEmptySites || instructions.length > 0);

    if (deliveriesError) {
        throw new Error('Une erreur est survenue lors de la récupération des livraisons');
    }

    return (
        <Container>
            <StickyHeaderContainer>
                <Header>
                    <HeaderTitle>
                        <PageTitle page={PAGES.deliveriesDailyPhoto} />
                    </HeaderTitle>

                    <HeaderContainer>
                        <SwitchFilterContainer>
                            <TotemLabel>Masquer les sites sans photos</TotemLabel>
                            <SwitchContainer>
                                <Switch
                                    onChange={() => setHideEmptySites(!hideEmptySites)}
                                    checked={hideEmptySites}
                                    onColor={colors.green}
                                    offColor={colors.pantinGrey}
                                />
                            </SwitchContainer>
                        </SwitchFilterContainer>

                        <SwitchFilterContainer>
                            <TotemLabel>Photos</TotemLabel>
                            <SegmentedControl
                                options={PHOTO_TIME_OPTIONS}
                                value={photoTimeFilter}
                                onChange={setPhotoTimeFilter}
                            />
                        </SwitchFilterContainer>

                        <DateContainer>
                            <TotemDateRangePicker
                                label="Période"
                                startDate={confirmedStartDate}
                                endDate={confirmedEndDate}
                                onConfirm={({ startDate, endDate }) => {
                                    setConfirmedStartDate(startDate);
                                    setConfirmedEndDate(endDate);
                                }}
                            />
                        </DateContainer>
                    </HeaderContainer>
                </Header>
            </StickyHeaderContainer>

            <SearchContainer>
                <TotemInput
                    label="Recherche"
                    onChange={setSearchValue}
                    placeholder="Nom du site ou de l'organisation"
                    value={searchValue}
                />
            </SearchContainer>
            <FiltersContainer>
                <Title>Filtres</Title>
                <FilterContainer>
                    <UltraSelect
                        source={TOTOPS_SITE_SOURCE}
                        value={selectedSiteId}
                        onChange={setSelectedSiteId}
                        placeholder="Filtrer par site"
                        label=""
                        width="250px"
                    />
                </FilterContainer>
                <FilterContainer>
                    <TotemSelect
                        placeholder="Filtrer par type d'arrêt"
                        value={selectedStopType}
                        options={STOP_TYPE_OPTIONS}
                        onChange={setSelectedStopType}
                        isClearable
                        width="250px"
                    />
                </FilterContainer>
                <FilterContainer>
                    <TotemSelect
                        placeholder="Filtrer par organisation"
                        value={selectedOrganizationName}
                        options={organizationOptions}
                        onChange={(value) => setSelectedOrganizationName(value)}
                        isClearable
                        width="250px"
                    />
                </FilterContainer>
                <FilterContainer>
                    <TotemSelect
                        placeholder="Filtrer par état"
                        value={selectedStates}
                        options={STATE_OPTIONS}
                        onChange={(values) => setSelectedStates(values)}
                        isMulti
                        isClearable
                        width="250px"
                    />
                </FilterContainer>
                <FilterContainer>
                    <TotemSelect
                        placeholder="Filtrer par type d'anomalie"
                        value={selectedIssueKeys}
                        options={ISSUE_TYPE_OPTIONS}
                        onChange={(values) => setSelectedIssueKeys(values)}
                        isMulti
                        isClearable
                        width="250px"
                    />
                </FilterContainer>
            </FiltersContainer>
            <SitesCount>
                {' '}
                {sortedDeliveries.length + taskSections.length} site
                {sortedDeliveries.length + taskSections.length > 1 ? 's' : ''} affiché
                {sortedDeliveries.length + taskSections.length > 1 ? 's' : ''} sur {deliveries.length} livraison
                {deliveries.length > 1 ? 's' : ''}
                {deliveriesWithPhotosCount !== deliveries.length &&
                    ` (${deliveriesWithPhotosCount} avec photos)`} et {tasks.length} tâche{tasks.length > 1 ? 's' : ''}
            </SitesCount>
            <Content>
                {deliveriesLoading && !deliveriesData ? (
                    <LoaderContainer>
                        <Loader />
                    </LoaderContainer>
                ) : (
                    sortedDeliveries.map((delivery) => (
                        <SectionContainer
                            key={delivery._id}
                            title={`${delivery.organizationName || delivery.siteName || delivery.label} (${
                                delivery.label
                            }) - ${formatDateForDisplay(delivery.deliveryDate)}`}
                            keepOpen
                        >
                            <>
                                <DetailLink path="delivery" value={delivery._id} name="Voir la livraison →" />
                                <PhotosSectionContent
                                    delivery={delivery}
                                    selectedStates={selectedStates}
                                    selectedIssueTypes={selectedIssueTypes}
                                    showBeforePhotos={showBeforePhotos}
                                    showAfterPhotos={showAfterPhotos}
                                />
                            </>
                        </SectionContainer>
                    ))
                )}
                {taskSections.map(({ task, instructions }) => (
                    <SectionContainer
                        key={task._id}
                        title={`${task.site?.organizationName || task.site?.name} (${
                            TASK_TYPE_LABELS[task.type]
                        }) - ${formatDateForDisplay(new Date(task.date))}`}
                        keepOpen
                    >
                        <>
                            <DetailLink to={`/tours/${task.date}`} name="Voir les tournées du jour →" />
                            {instructions.map(({ _id, label, photos }) => (
                                <div key={_id}>
                                    <TotemLabel>{label}</TotemLabel>
                                    <TotemGallery images={photos} />
                                </div>
                            ))}
                        </>
                    </SectionContainer>
                ))}
            </Content>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    background-color: ${({ theme }) => theme.backgroundColor};
    overflow: auto;
`;

const StickyHeaderContainer = styled.div`
    position: sticky;
    top: 0;
    z-index: 1;
    background-color: ${({ theme }) => theme.backgroundColor};
    box-shadow: ${({ theme }) => theme.boxShadow};
`;

const SitesCount = styled.div`
    padding: 10px 20px;
    font-size: 16px;
    font-weight: 600;
    color: ${({ theme }) => theme.ctaPrimaryTextColor};
`;

const Content = styled.div`
    padding: 0 20px;
    position: relative;

    & > :not(:first-child) {
        margin-top: 15px;
    }
`;
const HeaderContainer = styled.div`
    display: flex;
    gap: 30px;
    margin-left: 20px;
    align-items: flex-start;
`;

const FiltersContainer = styled.div`
    display: flex;
    gap: 5px;
    margin-left: 20px;
    align-items: center;
`;

const DateContainer = styled.div`
    min-width: 300px;
`;

const FilterContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
`;

const SwitchFilterContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
`;

const SwitchContainer = styled.div`
    margin-top: 5px;
`;

const SearchContainer = styled.div`
    padding: 15px 20px 10px 20px;
`;

const Title = styled.h3`
    margin: 0;
    font-size: 20px;
    font-weight: 800;
`;

const LoaderContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 40px;
`;
